import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, ChevronDown, Play } from "lucide-react";
import CommunityJournalsCarousel from "@/components/journals/CommunityJournalsCarousel";
import AuthModal from "@/components/auth/AuthModal";
import VideoModal from "@/components/landing/VideoModal";

// ============================================================================
// Rotating Titles Component for Landing Page
// ============================================================================

const ROTATING_TITLES = ["Document your progress.", "Learn out loud.", "Journal your learning.", "Learn with a personal tutor.", "Build your knowledge trail.", "Discuss. Practice. Recall."];
const RotatingTitle = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = useRef(false);

  // Check for reduced motion preference
  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Rotate titles every 3 seconds
  useEffect(() => {
    if (prefersReducedMotion.current || isPaused) return;
    const interval = setInterval(() => {
      setIsAnimating(true);

      // After exit animation completes, switch to next title
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % ROTATING_TITLES.length);
        setIsAnimating(false);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, [isPaused]);
  return <h1 className="min-h-[3.5rem] md:min-h-[3rem] flex items-center justify-center overflow-hidden px-4" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      <span className={`text-2xl md:text-3xl font-semibold text-slate-700 text-center transition-all duration-300 ease-in-out ${isAnimating ? 'opacity-0 -translate-y-1.5' : 'opacity-100 translate-y-0'}`}>
        {ROTATING_TITLES[currentIndex]}
      </span>
    </h1>;
};

// ============================================================================
// Landing Page Component
// ============================================================================

const Landing = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Hide Hotjar feedback widget for non-logged in users (landing page)
  useEffect(() => {
    const hideHotjar = () => {
      const hotjarWidget = document.querySelector('._hj_feedback_container') as HTMLElement;
      if (hotjarWidget) {
        hotjarWidget.style.display = 'none';
      }
    };
    
    // Try immediately and after a delay (Hotjar loads async)
    hideHotjar();
    const timeout = setTimeout(hideHotjar, 2000);
    const interval = setInterval(hideHotjar, 500);
    
    // Stop checking after 5 seconds
    const cleanup = setTimeout(() => clearInterval(interval), 5000);
    
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
      clearTimeout(cleanup);
      // Restore Hotjar when leaving landing page
      const hotjarWidget = document.querySelector('._hj_feedback_container') as HTMLElement;
      if (hotjarWidget) {
        hotjarWidget.style.display = '';
      }
    };
  }, []);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  const handleCreateJournal = () => {
    setAuthMode('signup');
    setShowAuthModal(true);
  };
  const handleLogin = () => {
    setAuthMode('login');
    setShowAuthModal(true);
  };
  return <div className="min-h-screen bg-[#F8F8F6] bg-noise flex flex-col">
      {/* Header */}
      <header className="w-full md:px-12 py-4 flex items-center justify-between px-[16px]">
        <img src="/Logo/sblogo.png" alt="StudiBudi" className="h-8 w-auto" />
        <button onClick={handleLogin} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 bg-white/60 hover:bg-white border border-slate-200/60 hover:border-slate-300 rounded-xl shadow-sm transition-all duration-150">
          Log in
        </button>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex justify-center md:px-12 lg:px-16 py-6 md:py-10 radial-frame px-[16px]">
        <div className="w-full max-w-[1600px] space-y-12 pt-8 md:pt-16 pb-6">
          {/* Rotating titles header */}
          <div className="text-center space-y-4">
            <RotatingTitle />
            <p className="text-sm text-slate-400 max-w-md mx-auto px-[16px]">
              Start with a topic or a community journal. StudiBudi helps you break it down, practice, and retain it.
            </p>
          </div>

          {/* Create New Learning Journal Card - Centered */}
          <div className="flex justify-center px-[16px] py-0">
            <div onClick={handleCreateJournal} className="bg-white/80 border border-slate-200/80 rounded-3xl p-8 cursor-pointer min-h-[220px] w-full max-w-[340px] flex flex-col items-center justify-center gap-5 animate-fade-in-up group transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 active:translate-y-0 active:transition-[transform] active:duration-100">
              {/* Hero Plus Icon */}
              <div className="w-16 h-16 rounded-2xl bg-slate-100/80 flex items-center justify-center group-hover:bg-blue-50 group-hover:border-blue-200/50 border border-transparent transition-all duration-300">
                <Plus className="w-8 h-8 text-slate-400 group-hover:text-blue-500 transition-colors duration-300" strokeWidth={2.5} />
              </div>
              {/* CTA Text */}
              <div className="text-center space-y-1.5">
                <h3 className="text-lg font-bold text-slate-700 group-hover:text-slate-900 transition-colors tracking-tight">
                  Create a learning journal
                </h3>
                <p className="text-sm text-slate-500 font-medium group-hover:text-slate-600 transition-colors opacity-80">
                  Start with a question, upload, or video
                </p>
              </div>
            </div>
          </div>

          {/* Watch Video Button */}
          <div className="flex justify-center -mt-4">
            <button
              onClick={() => setShowVideoModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors group"
            >
              <div className="w-6 h-6 rounded-full bg-slate-200/80 group-hover:bg-slate-300/80 flex items-center justify-center transition-colors">
                <Play className="w-3 h-3 text-slate-500 group-hover:text-slate-700 ml-0.5" fill="currentColor" />
              </div>
              Watch video
            </button>
          </div>

          {/* Community journals carousel */}
          <CommunityJournalsCarousel />

          {/* Product Hunt Badge */}
          <div className="flex justify-center">
            <a href="https://www.producthunt.com/products/studibudi?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-studibudi" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1054945&theme=light&t=1766879111558" alt="Studibudi - The world's first AI tutor! | Product Hunt" width="180" height="39" />
            </a>
          </div>

          {/* Video Thumbnail Section */}
          <div className="flex justify-center pt-8">
            <div className="w-full max-w-2xl space-y-4">
              <h2 className="text-lg font-semibold text-slate-600 text-center">
                See StudiBudi in action
              </h2>
              <button
                onClick={() => setShowVideoModal(true)}
                className="relative w-full aspect-video rounded-2xl overflow-hidden group cursor-pointer border border-slate-200/60 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                {/* Thumbnail image */}
                <img
                  src="https://vumbnail.com/1149570669.jpg"
                  alt="Watch demo video"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300" />
                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 flex items-center justify-center transition-all duration-300 shadow-lg">
                    <Play className="w-7 h-7 md:w-8 md:h-8 text-slate-700 ml-1" fill="currentColor" />
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="flex justify-center pt-8">
            <div className="w-full max-w-2xl space-y-8">
              <h2 className="text-2xl font-bold text-slate-800 text-center tracking-tight">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <FAQItem question="What's a learning journal?" answer="A learning journal is your personal space to explore any topic. Upload PDFs, add YouTube videos, or just start with a question. StudiBudi helps you break down complex concepts, create study materials, and track your progress—all in one place." />
                <FAQItem question="What's a personal AI tutor?" answer="Think of it as a brilliant study buddy who's always available. StudiBudi listens to you (literally—it's voice-first!), answers your questions, generates quizzes, creates visual explainers, and adapts to how you learn. It remembers what you've studied and connects ideas across topics." />
                <FAQItem question="How is this different from ChatGPT?" answer="ChatGPT is a general assistant. StudiBudi is laser-focused on learning. It works with your materials (PDFs, videos, notes), remembers your learning history, and creates structured content like quizzes and slide decks. It's built to help you actually retain and understand—not just get quick answers." />
                <FAQItem question="Is it really voice-first?" answer="Yes! You can talk to StudiBudi just like you'd talk to a tutor. Press and hold to speak, and it responds with voice. Perfect for when you're reviewing notes, working through problems, or just want to think out loud. Text chat is available too." />
                <FAQItem question="What can I learn with StudiBudi?" answer="Anything. Math, languages, coding, history, exam prep—you name it. Upload your course materials, paste a YouTube lecture, or just ask a question. Browse community journals to see how others are learning, and copy any journal to make it your own." />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full px-6 md:px-12 py-10 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <Link to="/terms" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
            Terms of Use
          </Link>
          <Link to="/privacy" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
            Privacy Policy
          </Link>
        </div>
        <p className="text-sm text-slate-400 font-medium">
          © 2025 StudiBudi. All rights reserved.
        </p>
      </footer>

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} initialMode={authMode} />
      
      {/* Video Modal */}
      <VideoModal open={showVideoModal} onOpenChange={setShowVideoModal} />
    </div>;
};

// ============================================================================
// FAQ Item Component
// ============================================================================

interface FAQItemProps {
  question: string;
  answer: string;
}
const FAQItem = ({
  question,
  answer
}: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white/60 border border-slate-200/60 rounded-xl overflow-hidden transition-all duration-200">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/80 transition-colors">
        <span className="font-medium text-slate-700">{question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="px-5 pb-4 text-sm text-slate-500 leading-relaxed">
          {answer}
        </p>
      </div>
    </div>;
};
export default Landing;